/**
 * Recovery from stale code-split chunks.
 *
 * The app lazy-loads its routes, so the bundle is split into hashed chunks
 * (chunk-7aa14194.23c61110.js). Those hashes change on every build and the
 * previous files are removed from hosting on deploy. A tab that was opened
 * before a deploy still runs the old app.js, so navigating to a screen it had
 * not visited yet requests a chunk that no longer exists and fails with:
 *
 *   ChunkLoadError: Loading chunk chunk-7aa14194 failed.
 *   Error: Loading CSS chunk chunk-358c48a9 failed.
 *
 * When that happens the router navigation is aborted, `afterEach` never runs
 * and the loading overlay is left on screen, so the app looks frozen until the
 * user closes and reopens it. Reloading pulls a fresh index.html and recovers,
 * but it is offered through a notice rather than done automatically: the error
 * surfaces while the user sits on a working page that may hold unsaved form
 * input, and reloading behind their back would discard it.
 *
 * A plain reload is enough to recover, and the caches are deliberately left
 * alone. Hosting serves index.html with `max-age=0` and the service worker
 * handles navigations network-first, so the reload always revalidates and gets
 * the current index.html; the chunk names it points at are absent from the old
 * service worker's precache manifest, so they bypass the precache route and come
 * straight from the network. Clearing the caches would add nothing and would
 * throw away the offline fallback that keeps the app usable when the reload is
 * attempted with no connection.
 *
 * The notice is built from plain DOM nodes with inline styles on purpose. It has
 * to render in a page whose stylesheet chunk may be the one that failed, and
 * before the app has mounted, so it cannot rely on app CSS or on Vue.
 */

import i18n from '@/libs/i18n'

const NOTICE_ID = 'chunk-recovery-notice'

const RECOVERY_FLAG = 'habit:chunk-recovery-at'

// A reload recovers within seconds, so a chunk failing again this soon after one
// means the reload did not help: right after a deploy a CDN edge can still be
// serving the previous index.html while the invalidation propagates. Offering the
// same button again would leave the user pressing it with nothing happening.
const RECOVERY_WINDOW_MS = 60000

const CHUNK_ERROR_PATTERN = /Loading (?:CSS )?chunk\s+\S+\s+failed|ChunkLoadError|Failed to fetch dynamically imported module/i

/**
 * The app renders on a light surface by default, but users can switch to the dark
 * skin and the choice is persisted here by the app-config store. Read it straight
 * from storage rather than from the store, since the notice also has to work
 * before the app has mounted.
 */
function isDarkSkin() {
  try {
    return localStorage.getItem('vuexy-skin') === 'dark'
  } catch (error) {
    // Private browsing modes can throw on localStorage access.
    return false
  }
}

/**
 * Invert the notice against the active skin. A light card on the default light
 * skin blends into the page, which is the whole thing the notice must not do.
 */
function noticePalette() {
  return isDarkSkin()
    ? {
      background: '#ffffff', title: '#2f3349', text: '#5e5873', close: '#b9b9c3',
    }
    : {
      background: '#2f3349', title: '#ffffff', text: '#d0d2d6', close: '#d0d2d6',
    }
}

const noticeStyle = palette => `
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2147483647;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: calc(100% - 32px);
  max-width: 520px;
  padding: 20px 24px;
  border-radius: 8px;
  border-left: 6px solid #4e8769;
  background: ${palette.background};
  box-shadow: 0 12px 44px rgba(34, 41, 47, 0.45);
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: ${palette.text};
  text-align: left;
`

const ACTION_STYLE = `
  flex-shrink: 0;
  margin-left: 16px;
  padding: 8px 16px;
  border: 0;
  border-radius: 5px;
  background: #4e8769;
  color: #ffffff;
  font: inherit;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
`

const closeStyle = palette => `
  flex-shrink: 0;
  margin-left: 4px;
  padding: 8px;
  border: 0;
  background: none;
  color: ${palette.close};
  font: inherit;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
`

/**
 * Whether an error was caused by a chunk that could not be downloaded.
 * @param {unknown} error
 * @returns {boolean}
 */
export function isChunkLoadError(error) {
  if (!error) return false
  if (error.name === 'ChunkLoadError') return true

  const message = typeof error === 'string' ? error : error.message
  return typeof message === 'string' && CHUNK_ERROR_PATTERN.test(message)
}

/**
 * The loading overlay is normally hidden by the router's `afterEach` hook, which
 * does not run when a navigation fails.
 */
function hideAppLoading() {
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
}

/**
 * Whether the user already asked for a reload and it left the app just as broken.
 */
function recoveryJustFailed() {
  try {
    const attemptedAt = Number(sessionStorage.getItem(RECOVERY_FLAG)) || 0
    return attemptedAt > 0 && Date.now() - attemptedAt < RECOVERY_WINDOW_MS
  } catch (error) {
    // Private browsing modes can throw on sessionStorage access.
    return false
  }
}

function reloadForUpdate() {
  try {
    sessionStorage.setItem(RECOVERY_FLAG, String(Date.now()))
  } catch (error) {
    // Without the flag the notice cannot escalate, but the reload still helps.
  }

  window.location.reload()
}

/**
 * @param {boolean} escalated whether a reload was already tried and did not work.
 */
function buildNotice(escalated) {
  const palette = noticePalette()

  const notice = document.createElement('div')
  notice.id = NOTICE_ID
  notice.setAttribute('role', 'alert')
  notice.style.cssText = noticeStyle(palette)

  const copy = document.createElement('div')
  copy.style.cssText = 'flex-grow: 1; min-width: 0;'

  const title = document.createElement('div')
  title.style.cssText = `margin-bottom: 4px; font-size: 16px; font-weight: 600; color: ${palette.title};`
  title.textContent = i18n.t(escalated ? 'message.appUpdateFailedTitle' : 'message.appUpdateTitle')

  const text = document.createElement('div')
  text.style.cssText = 'font-size: 13px;'
  text.textContent = i18n.t(escalated ? 'message.appUpdateFailedText' : 'message.appUpdateText')

  const close = document.createElement('button')
  close.type = 'button'
  close.style.cssText = closeStyle(palette)
  close.textContent = '×'
  close.setAttribute('aria-label', i18n.t('message.appUpdateClose'))
  close.addEventListener('click', () => notice.remove())

  copy.appendChild(title)
  copy.appendChild(text)
  notice.appendChild(copy)

  // Reloading has already been tried and failed, so drop the button: reopening
  // the app is the only thing left that helps, and the text now says so.
  if (!escalated) {
    const action = document.createElement('button')
    action.type = 'button'
    action.style.cssText = ACTION_STYLE
    action.textContent = i18n.t('message.appUpdateAction')
    action.addEventListener('click', () => {
      // The current document stays up while index.html is fetched, so report
      // progress and stop the button from being pressed again meanwhile.
      action.disabled = true
      action.textContent = i18n.t('message.appUpdateInProgress')
      reloadForUpdate()
    })
    notice.appendChild(action)
  }

  notice.appendChild(close)

  return notice
}

/**
 * Offer the user a reload. Repeated chunk failures reuse the same notice.
 */
function showUpdateNotice() {
  if (document.getElementById(NOTICE_ID)) return

  if (!document.body) {
    document.addEventListener('DOMContentLoaded', showUpdateNotice, { once: true })
    return
  }

  document.body.appendChild(buildNotice(recoveryJustFailed()))
}

/**
 * Take over a failed chunk load: stop showing the loading overlay so the page is
 * usable again, and offer the reload that fixes it.
 * @param {unknown} error
 * @returns {boolean} whether the error was a chunk failure and was handled.
 */
export function handleChunkError(error) {
  if (!isChunkLoadError(error)) return false

  hideAppLoading()
  showUpdateNotice()
  return true
}

/**
 * Catch chunk failures that do not go through the router, such as components
 * lazy-loaded from inside a page.
 */
export function registerChunkErrorHandlers() {
  window.addEventListener('unhandledrejection', event => {
    handleChunkError(event.reason)
  })

  window.addEventListener('error', event => {
    handleChunkError(event.error)
  })
}
